import { getDefaultStore } from 'jotai';
import { Store } from 'jotai/vanilla/store';

import { uiRoutes } from '@consumer-frontend/nc-api';
import {
  isDefined,
} from '@consumer-frontend/nc-utils';

import {
  archivalEligibleAtom,
  doesPasswordExistAtom,
  hasVerifiedIdentityAtom,
  incomeDataAtom,
  isEmailApplyEligibleAtom,
  isEmailValidatedAtom,
  isIncomeDetailsRequiredAtom,
  isReturningCustomerFlowAtom,
  registrationDataAtom,
  ssnMatchedAtom,
} from '../store';

/* These functions execute outside React's render cycle.
 `getDefaultStore()` is Jotai's documented mechanism for out-of-tree store access where hooks are unavailable.
 A hook-based routing layer (e.g. `useAtomCallback`) would eliminate the need for direct store access if the routing logic
 grows more complex, at the cost of coupling the routing config to a component lifecycle. */
const store = getDefaultStore();

export type ApplyRoutesKeyT = keyof typeof uiRoutes.apply;
export type RouteConfigKeyT = typeof uiRoutes.apply[ApplyRoutesKeyT];

export type RouteActionContextT = {
  store?: Store;
};

export type RouteActionT = (context?: RouteActionContextT) => (RouteConfigKeyT | undefined);

export type StepConfigT = {
  back: RouteActionT;
  next: RouteActionT;
  progress: () => [number, number] | [];
};

const isWisconsinAddress = (): boolean => {
  const registrationData = store.get(registrationDataAtom);

  return registrationData.address?.state === 'WI';
};

const hasIncome = (): boolean => !!store.get(incomeDataAtom).source;

const getBasicInfoNextRoute = (): RouteConfigKeyT => {
  const isEmailApplyEligible = store.get(isEmailApplyEligibleAtom);
  const oauthVerified = store.get(isEmailValidatedAtom);
  const doesPasswordExist = store.get(doesPasswordExistAtom);

  if (isEmailApplyEligible && !doesPasswordExist) { return uiRoutes.apply.phoneNumber; }
  if (!isEmailApplyEligible) { return uiRoutes.apply.unableToContinue; }
  if (doesPasswordExist && oauthVerified) { return uiRoutes.apply.verifyContactInfo; }
  if (doesPasswordExist) { return uiRoutes.apply.verifyEmail; }

  return uiRoutes.apply.unableToContinue;
};

export const getAddressNextRoute = (): RouteConfigKeyT => {
  // submission can occur on the wisconsin page to create/claim an account
  if (isWisconsinAddress()) { return uiRoutes.apply.maritalInformation; }
  const hasVerifiedIdentity = store.get(hasVerifiedIdentityAtom);

  // If the user has verified their identity (ie: returning customer), they then go to income source.
  return hasVerifiedIdentity ? uiRoutes.apply.incomeSource : uiRoutes.apply.verifyIdentity;
};

export const getVerifyContactInfoNextRoute = (): RouteConfigKeyT => {
  if (isWisconsinAddress()) { return uiRoutes.apply.verifyMaritalInformation; }

  return hasIncome() ? uiRoutes.apply.verifyIncome : uiRoutes.apply.incomeSource;
};

export const getVerifyMaritalInformationNextRoute = (): RouteConfigKeyT => {
  return hasIncome() ? uiRoutes.apply.verifyIncome : uiRoutes.apply.incomeSource;
};

const getMaritalInformationNextRoute = (): RouteConfigKeyT => {
  const isReturning = store.get(isReturningCustomerFlowAtom);
  const hasVerifiedIdentity = store.get(hasVerifiedIdentityAtom);

  if (hasVerifiedIdentity) { return uiRoutes.apply.incomeSource; }

  return isReturning ? uiRoutes.apply.verifyIncome : uiRoutes.apply.verifyIdentity;
};

const getIncomeSourceBackRoute = (): RouteConfigKeyT => {
  const isReturning = store.get(isReturningCustomerFlowAtom);
  const hasVerifiedIdentity = store.get(hasVerifiedIdentityAtom);

  if (hasVerifiedIdentity) {
    return isWisconsinAddress() ? uiRoutes.apply.maritalInformation : uiRoutes.apply.address;
  }

  if (!isReturning) { return uiRoutes.apply.verifyIdentity; }

  return isWisconsinAddress() ? uiRoutes.apply.maritalInformation : uiRoutes.apply.verifyContactInfo;
};

export const getVerifyEmailCodeNextRoute = (): RouteConfigKeyT => {
  const { emailCodeHasToken } = store.get(registrationDataAtom);

  return emailCodeHasToken ? uiRoutes.apply.verifyContactInfo : uiRoutes.apply.verifyIdentity;
};

export const getVerifyIdentityNextRoute = (): RouteConfigKeyT => {
  const isReturning = store.get(isReturningCustomerFlowAtom);
  const ssnMatched = store.get(ssnMatchedAtom);
  const archivalEligible = store.get(archivalEligibleAtom);

  if (!isReturning) { return uiRoutes.apply.incomeSource; }
  if (ssnMatched || archivalEligible) { return uiRoutes.apply.verifyContactInfo; }

  return uiRoutes.apply.incomeSource;
};

export const getVerifyIdentityBackRoute = (): RouteConfigKeyT | undefined => {
  const isReturning = store.get(isReturningCustomerFlowAtom);

  if (isReturning) { return undefined; }

  return isWisconsinAddress() ? uiRoutes.apply.maritalInformation : uiRoutes.apply.address;
};

export const getMaritalInfoBackRoute = (): RouteConfigKeyT => {
  const isReturning = store.get(isReturningCustomerFlowAtom);
  const hasVerifiedIdentity = store.get(hasVerifiedIdentityAtom);

  if (hasVerifiedIdentity) { return uiRoutes.apply.address; }
  if (isReturning) { return uiRoutes.apply.verifyContactInfo; }

  return uiRoutes.apply.address;
};

const getPhoneNumberBackRoute = (): RouteConfigKeyT => {
  const hasVerifiedIdentity = store.get(hasVerifiedIdentityAtom);

  if (hasVerifiedIdentity) { return uiRoutes.apply.verifyIdentity; }

  return uiRoutes.apply.basicInfo;
};

export const getVerifyIncomeNextRoute: RouteActionT = (context) => {
  const isReturning = context?.store?.get(isReturningCustomerFlowAtom);
  const isIncomeDetailsRequired = context?.store?.get(isIncomeDetailsRequiredAtom);

  if (isReturning && !isIncomeDetailsRequired) {
    return uiRoutes.apply.consents;
  }

  return uiRoutes.apply.incomeDetails;
};

export const getVerifyIncomeBackRoute: RouteActionT = (context) => {
  const isReturning = context?.store?.get(isReturningCustomerFlowAtom);

  if (!isReturning) { return uiRoutes.apply.verifyIdentity; }

  return isWisconsinAddress() ? uiRoutes.apply.verifyMaritalInformation : uiRoutes.apply.verifyContactInfo;
};

export const getIncomeDetailsBackRoute: RouteActionT = (context) => {
  const isReturning = context?.store?.get(isReturningCustomerFlowAtom);

  if (isReturning) {
    return uiRoutes.apply.verifyIncome;
  }

  return uiRoutes.apply.incomeSource;
};

export const getConsentsBackRoute: RouteActionT = (context) => {
  const isReturning = context?.store?.get(isReturningCustomerFlowAtom);
  const isIncomeDetailsRequired = context?.store?.get(isIncomeDetailsRequiredAtom);

  if (isReturning && !isIncomeDetailsRequired) {
    return uiRoutes.apply.verifyIncome;
  }

  return uiRoutes.apply.incomeDetails;
};

const selectNewOrReturningProgress = (
  newProgress: [number, number],
  returningProgress: [number, number] | [],
): [number, number] | [] => {
  const isReturning = store.get(isReturningCustomerFlowAtom);

  return isReturning ? returningProgress : newProgress;
};

export type RegistrationFlowT = Partial<Record<RouteConfigKeyT, StepConfigT>>;
export const registrationFlow: RegistrationFlowT = {
  [uiRoutes.apply.borrowingNeeds]: {
    back: () => undefined,
    next: () => uiRoutes.apply.basicInfo,
    progress: () => [],
  },
  [uiRoutes.apply.basicInfo]: {
    back: () => uiRoutes.apply.borrowingNeeds,
    next: getBasicInfoNextRoute,
    progress: () => [],
  },
  [uiRoutes.apply.phoneNumber]: {
    back: getPhoneNumberBackRoute,
    next: () => uiRoutes.apply.address,
    progress: () => [1, 6],
  },
  [uiRoutes.apply.address]: {
    back: () => uiRoutes.apply.phoneNumber,
    next: getAddressNextRoute,
    progress: () => [2, 6],
  },
  [uiRoutes.apply.maritalInformation]: {
    back: getMaritalInfoBackRoute,
    next: getMaritalInformationNextRoute,
    progress: () => selectNewOrReturningProgress([2, 6], [1, 3]),
  },
  [uiRoutes.apply.verifyIdentity]: {
    back: getVerifyIdentityBackRoute,
    next: getVerifyIdentityNextRoute,
    progress: () => selectNewOrReturningProgress([3, 6], []),
  },
  [uiRoutes.apply.incomeSource]: {
    back: getIncomeSourceBackRoute,
    next: () => uiRoutes.apply.incomeDetails,
    progress: () => selectNewOrReturningProgress([4, 6], [2, 3]),
  },
  [uiRoutes.apply.incomeDetails]: {
    back: getIncomeDetailsBackRoute,
    next: () => uiRoutes.apply.consents,
    progress: () => selectNewOrReturningProgress([5, 6], [2, 3]),
  },
  [uiRoutes.apply.consents]: {
    back: getConsentsBackRoute,
    next: () => undefined, // End of flow
    progress: () => selectNewOrReturningProgress([6, 6], [3, 3]),
  },

  // M2+
  // TODO: signinOptions page (before OTP verification)
  [uiRoutes.apply.verifyContactInfo]: {
    back: () => undefined,
    next: getVerifyContactInfoNextRoute,
    progress: () => [1, 3],
  },
  [uiRoutes.apply.verifyMaritalInformation]: {
    back: () => uiRoutes.apply.verifyContactInfo,
    next: getVerifyMaritalInformationNextRoute,
    progress: () => [1, 3],
  },
  [uiRoutes.apply.verifyIncome]: {
    back: getVerifyIncomeBackRoute,
    next: getVerifyIncomeNextRoute,
    progress: () => [2, 3],
  },
  [uiRoutes.apply.verifyEmail]: {
    back: () => undefined,
    next: () => uiRoutes.apply.verifyEmailCode,
    progress: () => [],
  },
  [uiRoutes.apply.verifyEmailCode]: {
    back: () => uiRoutes.apply.verifyEmail,
    next: getVerifyEmailCodeNextRoute,
    progress: () => [],
  },
};

export type IsInRegistrationFlowT = (route?: string | RouteConfigKeyT) => boolean;

export const isInRegistrationFlow: IsInRegistrationFlowT = (route): route is RouteConfigKeyT => {
  return isDefined(route) && route in registrationFlow;
};
